import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useListMailMessageNotes,
  useCreateMailMessageNote,
  useDeleteMailMessageNote,
  getListMailMessageNotesQueryKey,
  type MailMessage,
  type MailAccount,
} from "@workspace/api-client-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Mail as MailIcon,
  CheckSquare,
  Loader2,
  Trash2,
  StickyNote,
} from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { useToast } from "@/hooks/use-toast";
import { TriageBadge } from "@/components/triage-badge";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  message: MailMessage | null;
  account?: MailAccount;
  busy: boolean;
  onCreateTask: () => void;
  onOpenMail: () => void;
};

export function ActionDetailDialog({
  open,
  onOpenChange,
  message,
  account,
  busy,
  onCreateTask,
  onOpenMail,
}: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const messageId = message?.id ?? "";
  const notesQueryKey = getListMailMessageNotesQueryKey(messageId);

  const { data, isLoading } = useListMailMessageNotes(messageId, {
    query: {
      queryKey: notesQueryKey,
      enabled: open && !!messageId,
      // Match the card-badge staleTime so opening the dialog does not
      // trigger an immediate re-fetch when the count was just loaded.
      staleTime: 60_000,
    },
  });

  const create = useCreateMailMessageNote();
  const del = useDeleteMailMessageNote();

  const [draft, setDraft] = useState("");

  useEffect(() => {
    if (!open) setDraft("");
  }, [open]);

  if (!message) return null;

  const notes = data?.notes ?? [];

  const handleAdd = async () => {
    const content = draft.trim();
    if (!content) return;
    try {
      await create.mutateAsync({ id: message.id, data: { content } });
      setDraft("");
      queryClient.invalidateQueries({ queryKey: notesQueryKey });
    } catch (e) {
      toast({
        title: "Impossible d'ajouter l'annotation",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (noteId: string) => {
    try {
      await del.mutateAsync({ id: message.id, noteId });
      queryClient.invalidateQueries({ queryKey: notesQueryKey });
    } catch (e) {
      toast({
        title: "Impossible de supprimer",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    }
  };

  const sender = message.fromName
    ? `${message.fromName}${message.fromAddress ? ` <${message.fromAddress}>` : ""}`
    : (message.fromAddress ?? "(expéditeur inconnu)");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[85vh] flex flex-col"
        data-testid="action-detail-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-base sm:text-lg leading-snug">
            {message.subject || "(sans objet)"}
          </DialogTitle>
          <DialogDescription asChild>
            <div className="text-xs text-muted-foreground space-y-0.5 mt-1">
              <div>De : {sender}</div>
              {account && (
                <div className="inline-flex items-center gap-1">
                  <MailIcon className="w-3 h-3" />
                  {account.label}
                </div>
              )}
              {message.receivedAt && (
                <div>
                  Reçu : {format(new Date(message.receivedAt), "Pp", { locale: fr })}
                </div>
              )}
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-1.5">
          <TriageBadge category={message.triageCategory} short />
          {message.triageProjectHint && (
            <span className="text-[10px] text-muted-foreground truncate">
              · {message.triageProjectHint}
            </span>
          )}
        </div>

        {(message.triageSummary || message.snippet) && (
          <ScrollArea className="max-h-40 rounded-md border border-border/60 bg-muted/30 p-3">
            {message.triageSummary && (
              <p className="text-xs text-foreground whitespace-pre-wrap mb-2">
                {message.triageSummary}
              </p>
            )}
            {message.snippet && (
              <p className="text-xs text-muted-foreground whitespace-pre-wrap">
                {message.snippet}
              </p>
            )}
          </ScrollArea>
        )}

        <div className="flex flex-col gap-2 min-h-0 flex-1">
          <div className="flex items-center gap-2 text-sm font-medium text-foreground">
            <StickyNote className="w-4 h-4 text-primary" />
            Annotations
            <span className="text-xs text-muted-foreground font-normal">
              ({notes.length})
            </span>
          </div>

          <ScrollArea className="flex-1 min-h-[80px] max-h-60 rounded-md border border-border/60 p-2">
            {isLoading ? (
              <div className="text-xs text-muted-foreground p-2">Chargement…</div>
            ) : notes.length === 0 ? (
              <div
                className="text-xs text-muted-foreground p-2"
                data-testid="annotations-empty"
              >
                Aucune annotation pour le moment.
              </div>
            ) : (
              <ul className="space-y-2" data-testid="annotations-list">
                {notes.map((n) => (
                  <li
                    key={n.id}
                    className="group flex items-start gap-2 rounded-md bg-muted/40 px-2 py-1.5"
                    data-testid={`annotation-${n.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-foreground whitespace-pre-wrap break-words">
                        {n.content}
                      </p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        {format(new Date(n.createdAt), "Pp", { locale: fr })}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 opacity-0 group-hover:opacity-100 focus:opacity-100"
                      disabled={del.isPending}
                      onClick={() => handleDelete(n.id)}
                      data-testid={`annotation-delete-${n.id}`}
                      aria-label="Supprimer"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </ScrollArea>

          <div className="flex flex-col gap-1.5">
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Ajouter une annotation…"
              rows={2}
              className="resize-none text-sm"
              data-testid="annotation-input"
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                  e.preventDefault();
                  handleAdd();
                }
              }}
            />
            <div className="flex justify-end">
              <Button
                size="sm"
                onClick={handleAdd}
                disabled={!draft.trim() || create.isPending}
                data-testid="annotation-add"
              >
                {create.isPending ? (
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                ) : null}
                Ajouter
              </Button>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2 pt-2 border-t border-border/40">
          <Button
            variant="outline"
            size="sm"
            onClick={onOpenMail}
            data-testid="action-detail-open-mail"
          >
            <MailIcon className="w-3.5 h-3.5 mr-1.5" />
            Voir le mail
          </Button>
          <Button
            size="sm"
            onClick={onCreateTask}
            disabled={busy}
            data-testid="action-detail-create-task"
          >
            {busy ? (
              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
            ) : (
              <CheckSquare className="w-3.5 h-3.5 mr-1.5" />
            )}
            Créer une tâche
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
